import type { ApiClient } from '@carbonwebmail/api-client'
import type { SendingDayDto, SendingHistoryDto } from '@carbonwebmail/contracts'
import { BOUNCE_RATE_LIMIT, COMPLAINT_RATE_LIMIT } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import { Async } from '../../ui/Async.js'
import type { Bar as BarChartBar } from '../../ui/BarChart.js'
import { BarChart } from '../../ui/BarChart.js'
import { count, percent } from '../../ui/format.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Bounce and complaint rates against the lines that get a sender cut off.
 *
 * Three decisions here are about not lying with a number.
 *
 * **The rate is over the period, not an average of daily rates.** A day with
 * three sends and one bounce is 33%; averaging that against a day of forty
 * thousand produces a figure describing neither. So the totals are summed and
 * divided once.
 *
 * **A day with nothing sent shows nothing**, not 0%. Zero is a claim — "we sent
 * and nothing bounced" — and a row of reassuring zeroes across a fortnight
 * nobody sent in is the most misleading thing this screen could draw.
 *
 * **Bounces are counted on the day they were reported**, not the day of the
 * send they belong to. A provider webhook arrives minutes or days later, and
 * attributing it backwards would rewrite a figure somebody has already read.
 * The screen says so rather than leaving the discrepancy to be discovered.
 */
export function ReputationPage({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.sendingHistory(), [client])
  const { state } = useLoaded<SendingHistoryDto>(
    load,
    'We could not load your sending history. Please try again in a moment.',
  )

  return (
    <>
      <header className="mb-6">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Reputation</h1>
        <p className="mt-1 text-[13px] text-t2">
          The last thirty days against the levels at which mailbox providers stop delivering for
          you. Nothing here pauses sending automatically.
        </p>
      </header>

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Loading…</p>}
        onRetry={() => window.location.reload()}
      >
        {(history) => <Rates days={history.days} />}
      </Async>
    </>
  )
}

function Rates({ days }: { readonly days: readonly SendingDayDto[] }) {
  const sent = days.reduce((total, day) => total + day.accepted, 0)
  const bounced = days.reduce((total, day) => total + day.bounced, 0)
  const complained = days.reduce((total, day) => total + day.complained, 0)

  if (sent === 0) {
    return (
      <div className="rounded-card border border-line bg-surface p-10 text-center">
        <h2 className="font-bold text-[15px] text-t1">Nothing sent in the last thirty days</h2>
        <p className="mx-auto mt-2 max-w-md text-[13px] text-t2 leading-relaxed">
          A rate needs something to be a rate of. Send a campaign and this fills in — it is the
          screen to check before a large one.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-3">
        <Rate label="Delivered" value={count(sent)} note="Accepted by the provider." />
        <Rate
          label="Bounce rate"
          value={percent(bounced / sent)}
          note={`Providers act around ${percent(BOUNCE_RATE_LIMIT, 0)}.`}
          over={bounced / sent >= BOUNCE_RATE_LIMIT}
        />
        <Rate
          label="Complaint rate"
          value={percent(complained / sent, 2)}
          note={`Providers act around ${percent(COMPLAINT_RATE_LIMIT, 1)}.`}
          over={complained / sent >= COMPLAINT_RATE_LIMIT}
        />
      </div>

      <p className="mt-3 text-[12px] text-t3">
        Rates are over the whole period, not an average of each day — a quiet day with one bounce is
        not a 100% bounce rate. Bounces and complaints count on the day the provider reported them,
        which can be days after the send.
      </p>

      <h2 className="mt-8 mb-3 font-bold text-[15px] text-t1">Day by day</h2>
      <BarChart bars={days.map(toBar)} />
    </>
  )
}

function dayTotal(day: SendingDayDto): number {
  return day.accepted + day.rejected + day.failed + day.unknown
}

function Rate({
  label,
  value,
  note,
  over,
}: {
  readonly label: string
  readonly value: string
  readonly note: string
  readonly over?: boolean
}) {
  return (
    <div
      className={`rounded-card border p-4 ${over === true ? 'border-danger bg-danger-soft' : 'border-line bg-surface'}`}
    >
      <p className="font-semibold text-[12px] text-t2">{label}</p>
      <p
        className={`mt-1 font-bold font-num text-[24px] tabular-nums ${over === true ? 'text-danger' : 'text-t1'}`}
      >
        {value}
      </p>
      <p className="mt-1 text-[12px] text-t3">{note}</p>
    </div>
  )
}

/**
 * One day, as a bar.
 *
 * A day with a bounce or a complaint on it is the warning colour, because that
 * is what this screen is read for — the figures above say how much, and the
 * shape below says when.
 */
function toBar(day: SendingDayDto): BarChartBar {
  const trouble = day.bounced + day.complained

  return {
    key: day.day,
    value: dayTotal(day),
    tone: trouble > 0 ? 'warn' : 'ok',
    title: `${count(dayTotal(day))} sent, ${count(trouble)} bounced or complained`,
    label: String(new Date(day.day).getUTCDate()),
  }
}
